import React from 'react';
import PropTypes from 'prop-types';

import {
  LISTING_DATA_SOURCE_SUNROOM,
} from 'common/listingConstants';

import makeNumbersRange from 'common/util/makeNumbersRange';
import lodashGet from 'lodash/fp/get';

import {
  numberWithCommas,
  formatPrice,
} from 'common/formatters';

import {
  getListingFieldsForDisplay,
  getProratedRentMonth,
  getMarketSecurityDepositLabel,
} from 'common/listingHelpers';

import {
  listingFormFields,
} from 'common/listingFormFields';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrCardOld from 'client/components/SrCardOld';
import SrEmoji from 'client/components/SrEmoji';
import SrIcon from 'client/components/SrIcon';
import SrTooltip from 'client/components/SrTooltip';
import SrSeparator from 'client/components/SrSeparator';

const listingFormFieldsById = listingFormFields.reduce((memo, formField) => ({
  ...memo,
  [formField.id]: formField,
}), {});

const extraListingFees = [
  'valetTrashFee',
  'pestControlFee',
  'wasteWaterFee',
  'monthlyCommunityFee',
  'carportFee',
  'reservedParkingSpotFee',
  'bikeRackFee',
  'electricFee',
  'gasFee',
  'waterAndSewerFee',
  'cableAndInternetCombinedFee',
  'cableFee',
  'internetFee',
  'packageLockerSystemFee',
  'coveredParkingFee',
  'storageFee',
  'surfaceParkingFee',
  'garageAttachedParkingSpotFee',
  'garageDetachedParkingSpotFee',
];

const SrListingExpensesCard = ({
  listing,
  marketsById,
  residentBenefitsPackageAmount,
  proratedRentFirstMonth,
}) => {
  const listingFieldsForDisplay = getListingFieldsForDisplay({ listing });
  const disabledSettings = lodashGet(`${listing.marketId}.settings.listing.disabled`, marketsById) || [];
  const ignoreDisabledSettings = listing.dataSource === LISTING_DATA_SOURCE_SUNROOM;
  let totalMoveInCost = listingFieldsForDisplay.price + (listing.applicationFee || 0);
  const shouldRenderRow = (settingName) => {
    return ignoreDisabledSettings || !disabledSettings.includes(settingName);
  };
  let monthRentTitle = '1st Month\'s Rent';
  let monthRentValue = formatPrice(listingFieldsForDisplay.price);

  if (proratedRentFirstMonth) {
    const proratedRent = getProratedRentMonth(listingFieldsForDisplay.price);

    monthRentTitle = (
      <SrFlex align='center'>
        Prorated Rent
        <SrTooltip
          icon={<SrIcon className='question-mark-circle-small' />}
          className='tooltip'
        >
          Calculated based on the current day you’re viewing this page.
          So this amount may be different than the prorated rent you will pay,
          which will be based on the date you choose to move in.
        </SrTooltip>
      </SrFlex>
    );
    monthRentValue = formatPrice(proratedRent);
    totalMoveInCost = proratedRent + (listing.applicationFee || 0);
  }

  const expensesList = [
    {
      title: monthRentTitle,
      value: monthRentValue,
    },
  ];

  if (shouldRenderRow('securityDeposit')) {
    totalMoveInCost += listingFieldsForDisplay.securityDeposit || 0;
    expensesList.push({
      title: getMarketSecurityDepositLabel(marketsById[listing.marketId], true),
      value: formatPrice(listingFieldsForDisplay.securityDeposit || 0),
    });
  }

  if (shouldRenderRow('petDeposit')) {
    totalMoveInCost += listing.petDeposit || 0;
    expensesList.push({
      title: (
        <SrFlex align='center'>
          Pet Deposit
          <SrTooltip
            icon={<SrIcon className='question-mark-circle-small' />}
            className='tooltip'
          >
            Some landlords require this fee per pet.
          </SrTooltip>
        </SrFlex>
      ),
      value: `$${makeNumbersRange(listing.petDeposit, listing.petDepositMax)}`,
    });
  }

  if (typeof listing.petFee === 'number' && listing.petFee > 0) {
    totalMoveInCost += listing.petFee || 0;
    expensesList.push({
      title: 'Pet Fee (non-refundable)',
      value: `$${makeNumbersRange(listing.petFee, listing.petFeeMax)}`,
    });
  }

  if (shouldRenderRow('monthlyPetRent')) {
    totalMoveInCost += listing.monthlyPetRent || 0;
    expensesList.push({
      title: (
        <SrFlex align='center'>
          Monthly Pet Rent
          <SrTooltip
            icon={<SrIcon className='question-mark-circle-small' />}
            className='tooltip'
          >
            Some landlords require this fee per pet.
          </SrTooltip>
        </SrFlex>
      ),
      value: formatPrice(listing.monthlyPetRent || 0),
    });
  }

  if (shouldRenderRow('administrationFee')) {
    totalMoveInCost += listing.administrationFee || 0;
    expensesList.push({
      title: 'Administration Fee',
      value: formatPrice(listing.administrationFee || 0),
    });
  }

  if (shouldRenderRow('applicationFee')) {
    expensesList.push({
      title: (
        <SrFlex align='center'>
          Application Fee
          {listing.dataSource !== LISTING_DATA_SOURCE_SUNROOM && (
            <SrTooltip
              icon={<SrIcon className='question-mark-circle-small' />}
              className='tooltip'
            >
              All of this goes to the landlord. Sunroom does not make money from this fee.
            </SrTooltip>
          )}
        </SrFlex>
      ),
      value: `$${listing.applicationFee || 0}`,
    });
  }

  if (residentBenefitsPackageAmount) {
    totalMoveInCost += residentBenefitsPackageAmount;
    expensesList.push({
      title: 'Monthly Resident Benefits Package',
      value: formatPrice(residentBenefitsPackageAmount),
    });
  }

  extraListingFees.forEach((listingFee) => {
    const fee = listing[listingFee];
    const formField = listingFormFieldsById[listingFee];

    if (typeof fee === 'number' && fee > 0 && formField) {
      totalMoveInCost += fee;
      expensesList.push({
        title: formField.title,
        value: formatPrice(fee),
      });
    }
  });

  return (
    <SrCardOld className='SrListingExpensesCard'>
      <SrFlex className='cardHeader' justify='between'>
        <SrFlex direction='col' justify='between'>
          <SrText color='green' size='2xl' weight='bold'>
            ${numberWithCommas(Math.round(totalMoveInCost * 100) / 100)}
          </SrText>
          <SrBox marginTop={4}>
            <SrText color='green' weight='medium' font='whitney-sc'>
              total move-in cost
            </SrText>
          </SrBox>
        </SrFlex>
        <SrBox marginTop={-4} marginRight={-1}>
          <SrEmoji className='emoji' emoji='💰' />
        </SrBox>
      </SrFlex>
      <SrSeparator />
      <SrBox className='cardBody'>
        {expensesList.map((expense, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <SrFlex key={i} justify='between' className='expense'>
            <SrText>
              {expense.title}
            </SrText>
            <SrText weight='bold'>
              {expense.value}
            </SrText>
          </SrFlex>
        ))}
      </SrBox>
    </SrCardOld>
  );
};

SrListingExpensesCard.propTypes = {
  listing: PropTypes.object.isRequired,
  marketsById: PropTypes.object.isRequired,
  residentBenefitsPackageAmount: PropTypes.number,
  proratedRentFirstMonth: PropTypes.bool,
};

SrListingExpensesCard.displayName = 'SrListingExpensesCard';

export default SrListingExpensesCard;
